import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import UserLanguageSwitcher from '../components/common/UserLanguageSwitcher';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import '../components/dashboard/Dashboard.css';
import './FinancialDetailsPage.css';

const FinancialDetailsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [user, setUser] = useState({ currency: 'None' });
    const [loading, setLoading] = useState(true);

    const [summary, setSummary] = useState({ inflow: 0, outflow: 0, net: 0 });

    useEffect(() => {
        fetchProfile();
        fetchTransactions();
    }, []);

    useEffect(() => {
        if (transactions.length > 0) {
            calculateSummary();
        }
    }, [transactions]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        const pad = (n) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const fetchProfile = async () => {
        try {
            const data = await userService.getProfile();
            if (data.success) {
                setUser(data.user);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        }
    };

    const fetchTransactions = async () => {
        try {
            const data = await userService.getTransactions();
            if (data.success) {
                setTransactions(data.transactions || []);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
            toast.error(t('failed_load_financial', 'Failed to load financial records'));
        } finally {
            setLoading(false);
        }
    };

    const calculateSummary = () => {
        if (!transactions || !Array.isArray(transactions)) return;
        let inflow = 0;
        let outflow = 0;

        transactions.forEach(tx => {
            const amount = Math.abs(tx.amount);
            if (['deposit', 'trade_win', 'trade_profit'].includes(tx.type)) {
                inflow += amount;
            } else if (['withdrawal', 'trade_loss', 'trade'].includes(tx.type)) {
                outflow += amount;
            }
        });

        setSummary({
            inflow,
            outflow,
            net: inflow - outflow
        });
    };



    const getTransactionIcon = (type) => {
        switch (type) {
            case 'deposit': return 'bi-wallet2';
            case 'withdrawal': return 'bi-box-arrow-up-right';
            case 'trade_win': return 'bi-graph-up-arrow';
            case 'trade_loss': return 'bi-graph-down-arrow';
            case 'trade_profit': return 'bi-cash-stack';
            case 'trade': return 'bi-pcn-chart';
            default: return 'bi-file-earmark-text';
        }
    };

    // Helper to group transactions by date
    const groupTransactionsByDate = (transactions = []) => {
        if (!transactions || !Array.isArray(transactions)) return {};
        const groups = {};
        transactions.forEach(transaction => {
            const date = new Date(transaction.createdAt);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            let dateKey = format(date, 'MMM dd, yyyy');
            if (date.toDateString() === today.toDateString()) {
                dateKey = t('today', 'Today');
            } else if (date.toDateString() === yesterday.toDateString()) {
                dateKey = t('yesterday', 'Yesterday');
            }

            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(transaction);
        });
        return groups;
    };

    const getAmountColor = (type) => {
        if (['deposit', 'trade_win', 'trade_profit'].includes(type)) return '#0ecb81';
        if (['withdrawal', 'trade_loss', 'trade'].includes(type)) return '#f6465d';
        return '#ffffff';
    };

    const currencyMap = {
        'None': '', 'USD': '$', 'GBP': '£', 'BTC': '₿', 'EUR': '€', 'CHF': '₣',
        'KRW': '₩', 'TRY': '₺', 'INR': '₹', 'CNY': '¥', 'THB': '฿',
        'RUB': '₽', 'UAH': '₴', 'BDT': '৳', 'JPY': '¥', 'ILS': '₪',
        'AED': 'د.إ', 'SAR': 'ر.س'
    };
    const currencySymbol = user?.username !== undefined && user?.currency !== undefined ? (currencyMap[user.currency] !== undefined ? currencyMap[user.currency] : '') : '';

    const groupedTransactions = groupTransactionsByDate(transactions);

    return (
        <div className="financial-details-page min-vh-100" style={{ backgroundColor: '#0b0e11', paddingBottom: '90px' }}>
            {/* Majestic Header */}
            <div className="majestic-header d-flex align-items-center px-3 py-3 justify-content-between">
                <div style={{ width: '40px' }}>
                    <i className="bi bi-chevron-left text-white fs-4 cursor-pointer" onClick={() => navigate(-1)}></i>
                </div>
                <div className="flex-grow-1 text-center d-flex flex-column align-items-center justify-content-center">
                    <h1 className="majestic-header-title m-0" style={{ fontSize: '18px' }}>
                        {t('account_change_records', 'Account Records')}
                    </h1>
                    <div className="text-warning fw-bold" style={{ fontSize: '12px' }}>
                        {currencySymbol}{user.accountBalance ? Number(user.accountBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    </div>
                </div>
                <div style={{ width: '40px' }} className="d-flex justify-content-end">
                    <UserLanguageSwitcher />
                </div>
            </div>

            <div className="container py-4">
                {/* Summary Dashboard */}
                {!loading && transactions.length > 0 && (
                    <div className="row g-3 mb-4 animate-fade-in">
                        <div className="col-6">
                            <div className="p-3 rounded-4 h-100 position-relative overflow-hidden"
                                style={{
                                    background: 'linear-gradient(165deg, rgba(14, 203, 129, 0.1) 0%, rgba(14, 203, 129, 0.02) 100%)',
                                    border: '1px solid rgba(14, 203, 129, 0.2)',
                                    boxShadow: '0 8px 32px rgba(14, 203, 129, 0.05)'
                                }}>
                                <div className="text-success small mb-1 opacity-75 fw-bold" style={{ fontSize: '11px', letterSpacing: '1px' }}>{t('total_inflow', 'TOTAL INFLOW')}</div>
                                <div className="text-white fw-bold fs-5">+{currencySymbol}{summary.inflow.toFixed(2)}</div>
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="p-3 rounded-4 h-100 position-relative overflow-hidden"
                                style={{
                                    background: 'linear-gradient(165deg, rgba(246, 70, 93, 0.1) 0%, rgba(246, 70, 93, 0.02) 100%)',
                                    border: '1px solid rgba(246, 70, 93, 0.2)',
                                    boxShadow: '0 8px 32px rgba(246, 70, 93, 0.05)'
                                }}>
                                <div className="text-danger small mb-1 opacity-75 fw-bold" style={{ fontSize: '11px', letterSpacing: '1px' }}>{t('total_outflow', 'TOTAL OUTFLOW')}</div>
                                <div className="text-white fw-bold fs-5">-{currencySymbol}{summary.outflow.toFixed(2)}</div>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('loading', 'Loading...')}</span>
                        </div>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-5 opacity-50">
                        <i className="bi bi-file-earmark-x text-white mb-3" style={{ fontSize: '48px' }}></i>
                        <div className="text-white small">{t('no_records_found', 'No records found')}</div>
                    </div>
                ) : (
                    <div className="transaction-list">
                        {Object.keys(groupedTransactions).map((dateKey, index) => (
                            <div key={dateKey} className="mb-4">
                                <div className="d-flex align-items-center gap-2 mb-3 ps-2 opacity-75">
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#00bfa5' }}></div>
                                    <h6 className="text-white mb-0 fw-bold" style={{ fontSize: '14px', letterSpacing: '0.5px' }}>{dateKey}</h6>
                                    <div className="flex-grow-1" style={{ height: '1px', background: 'linear-gradient(90deg, rgba(255,255,255,0.1), transparent)' }}></div>
                                </div>

                                {groupedTransactions[dateKey].map((item) => {
                                    // Determine dynamic styles based on type
                                    const isPositive = ['deposit', 'trade_win', 'trade_profit'].includes(item.type);
                                    const isNegative = ['withdrawal', 'trade_loss', 'trade'].includes(item.type);

                                    // Restore missing content variables
                                    let remark = item.description || item.type;
                                    if (item.type === 'withdrawal') remark = t('request_withdrawal', 'Request withdrawal');
                                    if (item.type === 'trade_win' || item.type === 'trade_loss') remark = t('contract_settlement', 'Contract settlement');
                                    if (item.type === 'trade') remark = t('purchase_contract', 'Purchase contract');

                                    const amount = Math.abs(item.amount).toFixed(2);
                                    const sign = isPositive ? '+' : isNegative ? '-' : '';

                                    const glowColor = isPositive ? 'rgba(14, 203, 129, 0.15)' : isNegative ? 'rgba(246, 70, 93, 0.15)' : 'rgba(255, 255, 255, 0.05)';
                                    const borderColor = isPositive ? 'rgba(14, 203, 129, 0.3)' : isNegative ? 'rgba(246, 70, 93, 0.3)' : 'rgba(255, 255, 255, 0.1)';
                                    const iconGradient = isPositive
                                        ? 'linear-gradient(135deg, rgba(14, 203, 129, 0.2), rgba(14, 203, 129, 0.05))'
                                        : isNegative
                                            ? 'linear-gradient(135deg, rgba(246, 70, 93, 0.2), rgba(246, 70, 93, 0.05))'
                                            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02))';

                                    // Determine dynamic label for amount
                                    let amountLabel = t('amount', 'Amount');
                                    if (item.type === 'trade_win' || item.type === 'trade_profit') amountLabel = t('profit_amount', 'Profit Amount');
                                    if (item.type === 'trade_loss') amountLabel = t('loss_amount', 'Loss Amount');
                                    if (item.type === 'withdrawal') amountLabel = t('withdrawal_amount', 'Withdrawal Amount');
                                    if (item.type === 'deposit') amountLabel = t('deposit_amount', 'Deposit Amount');
                                    if (item.type === 'trade') amountLabel = t('trade_amount', 'Trade Amount');

                                    return (
                                        <div key={item.id} className="card border-0 mb-3 overflow-hidden animate-fade-in hover-card"
                                            style={{
                                                background: 'linear-gradient(165deg, rgba(22, 27, 38, 0.95) 0%, rgba(18, 22, 31, 0.8) 100%)',
                                                borderRadius: '20px',
                                                boxShadow: `0 10px 40px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)`,
                                                border: `1px solid ${borderColor}`,
                                                backdropFilter: 'blur(10px)',
                                                position: 'relative',
                                                animationDelay: `${index * 0.1}s`,
                                                borderLeft: `3px solid ${isPositive ? '#0ecb81' : isNegative ? '#f6465d' : 'transparent'}`
                                            }}>
                                            {/* Ambient Glow Effect */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '-50%',
                                                left: isPositive ? '-20%' : 'auto',
                                                right: isNegative ? '-20%' : 'auto',
                                                width: '60%',
                                                height: '200%',
                                                background: `radial-gradient(circle, ${glowColor} 0%, transparent 60%)`,
                                                transform: 'rotate(25deg)',
                                                pointerEvents: 'none',
                                                zIndex: 0,
                                                opacity: 0.6
                                            }}></div>

                                            <div className="card-body p-4 position-relative" style={{ zIndex: 1 }}>
                                                {/* Card Header: Icon + Type + Date */}
                                                <div className="d-flex align-items-center justify-content-between mb-4 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                                                            style={{
                                                                width: '54px',
                                                                height: '54px',
                                                                background: iconGradient,
                                                                border: `1px solid ${borderColor}`,
                                                                boxShadow: `0 0 20px -5px ${glowColor}`
                                                            }}>
                                                            <i className={`bi ${getTransactionIcon(item.type)}`} style={{ fontSize: '24px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}></i>
                                                        </div>
                                                        <div>
                                                            <div className="text-white fw-bold text-capitalize" style={{ fontSize: '18px', letterSpacing: '0.5px', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                                                                {t(item.type, item.type.replace('_', ' '))}
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2 mt-1">
                                                                {format(new Date(item.createdAt), 'yyyy-MM-dd HH:mm:ss')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="badge rounded-pill px-3 py-2 d-flex align-items-center gap-2"
                                                        style={{
                                                            background: 'rgba(255,255,255,0.05)',
                                                            border: `1px solid ${borderColor}`,
                                                            boxShadow: `0 0 15px -5px ${glowColor}`
                                                        }}>
                                                        <span className="sc-indicator" style={{ background: isPositive ? '#0ecb81' : isNegative ? '#f6465d' : '#fff' }}></span>
                                                        <span style={{
                                                            color: item.status === 'frozen' ? '#0dcaf0' : (isPositive ? '#0ecb81' : isNegative ? '#f6465d' : '#fff'),
                                                            fontSize: '11px',
                                                            fontWeight: '700',
                                                            letterSpacing: '1px'
                                                        }}>{item.status === 'frozen' ? t('frozen', 'FROZEN') : t('success', 'SUCCESS')}</span>
                                                    </div>
                                                </div>

                                                {/* Card Body: Detailed Rows */}
                                                <div className="d-flex flex-column gap-3">


                                                    {/* Profit Amount - Highlighted with Shimmer */}
                                                    <div className="d-flex justify-content-between align-items-center p-3 rounded-4 position-relative overflow-hidden"
                                                        style={{
                                                            background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%)',
                                                            borderLeft: `4px solid ${isPositive ? '#0ecb81' : isNegative ? '#f6465d' : '#666'}`
                                                        }}>
                                                        <div className="shimmer-overlay"></div>
                                                        <div className="text-white fw-bold" style={{ fontSize: '14px', letterSpacing: '0.3px' }}>
                                                            {amountLabel}
                                                        </div>
                                                        <div className="fw-bold" style={{
                                                            fontSize: '20px',
                                                            color: getAmountColor(item.type, item.amount),
                                                            textShadow: `0 0 25px ${glowColor}`,
                                                            letterSpacing: '-0.5px'
                                                        }}>
                                                            {sign}{currencySymbol}{amount}
                                                        </div>
                                                    </div>

                                                    {/* Remark - Enhanced Pill Style */}
                                                    <div className="d-flex justify-content-between align-items-center px-2">
                                                        <div className="d-flex align-items-center gap-2" style={{ fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.7)' }}>
                                                            <i className="bi bi-info-circle opacity-50" style={{ fontSize: '12px' }}></i>
                                                            {t('remark', 'Remark')}
                                                        </div>
                                                        <div className="text-white text-end px-3 py-1 rounded-pill"
                                                            style={{
                                                                fontSize: '12px',
                                                                background: 'rgba(255,255,255,0.05)',
                                                                border: '1px solid rgba(255,255,255,0.05)',
                                                                letterSpacing: '0.5px'
                                                            }}>
                                                            {remark}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FinancialDetailsPage;
